import { ConversationResponse, ConversationThreadResponse } from "@/types/api";
import { chatLogger, apiLogger } from "./logger";

export const debugConversation = (conversation: ConversationResponse) => {
  chatLogger.group("Debug Conversation");
  chatLogger.log("ID:", conversation.id);
  chatLogger.log("Name:", conversation.name);
  chatLogger.log("Lead ID:", conversation.leadid);
  chatLogger.log("Use AI:", conversation.useai);
  chatLogger.log("Avatar URL:", conversation.avatarUrl);
  chatLogger.log("Last Message:", conversation.lastMessage);
  chatLogger.log("Last Message Timestamp:", conversation.lastMessageTimestamp);

  // Formatar o timestamp para data legível se existir
  if (conversation.lastMessageTimestamp) {
    const timestamp = parseInt(conversation.lastMessageTimestamp);
    if (!isNaN(timestamp)) {
      const date = new Date(
        timestamp.toString().length <= 10 ? timestamp * 1000 : timestamp
      );
      chatLogger.log("Last Message Date:", date.toLocaleString());

      // Calcular quanto tempo atrás
      const now = Date.now();
      const diffMs = now - date.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      chatLogger.log("Hours since last message:", diffHours.toFixed(2));
      chatLogger.log("Status:", diffHours < 24 ? "🟢 Recent" : "🔴 Inactive");
    }
  }

  chatLogger.groupEnd();
};

export const debugConversationThread = (thread: ConversationThreadResponse) => {
  chatLogger.group("Debug Conversation Thread");
  chatLogger.log("ID:", thread.id);
  chatLogger.log("Name:", thread.name);
  chatLogger.log("Lead ID:", thread.leadid);
  chatLogger.log("Use AI:", thread.useai);
  chatLogger.log("Avatar URL:", thread.avatarUrl);
  chatLogger.log("Messages Count:", thread.messages?.length || 0);

  if (thread.messages && thread.messages.length > 0) {
    chatLogger.group("Messages");
    thread.messages.forEach((message, index) => {
      chatLogger.log(`Message ${index + 1}:`, {
        id: message.id,
        origin: message.origin,
        destination: message.destination,
        text:
          message.text.substring(0, 50) +
          (message.text.length > 50 ? "..." : ""),
        timestamp: new Date(parseInt(message.timestamp)).toLocaleString(),
      });
    });
    chatLogger.groupEnd();
  }

  chatLogger.groupEnd();
};

export const debugApiError = (error: any, context: string) => {
  apiLogger.group(`API Error - ${context}`);
  apiLogger.log("Status:", error.response?.status);
  apiLogger.log("Status Text:", error.response?.statusText);
  apiLogger.log("URL:", error.config?.url);
  apiLogger.log("Method:", error.config?.method?.toUpperCase());
  apiLogger.log("Headers:", error.config?.headers);

  // Detalhes específicos do erro 500
  if (error.response?.status === 500) {
    apiLogger.group("Server Error Details");
    apiLogger.log("Error Type:", error.response?.data?.error);
    apiLogger.log("Message:", error.response?.data?.message);
    apiLogger.log("Timestamp:", error.response?.data?.timestamp);
    apiLogger.log("Details:", error.response?.data?.details);

    // Se há detalhes adicionais, expandir
    if (error.response?.data?.details) {
      apiLogger.log("Stack Trace:", error.response?.data?.details?.stackTrace);
      apiLogger.log("Root Cause:", error.response?.data?.details?.rootCause);
      apiLogger.log("SQL Error:", error.response?.data?.details?.sqlError);
    }
    apiLogger.groupEnd();
  }

  apiLogger.log("Full Error:", error);
  apiLogger.groupEnd();

  // Sugestões baseadas no tipo de erro
  if (error.response?.status === 500) {
    apiLogger.group("Possíveis Soluções");
    apiLogger.log("1. Verificar se o banco de dados está rodando");
    apiLogger.log("2. Verificar se há dados de teste na tabela conversations");
    apiLogger.log("3. Verificar logs do backend para mais detalhes");
    apiLogger.log("4. Verificar se as entidades estão mapeadas corretamente");
    apiLogger.log(
      "5. Verificar se há problemas de relacionamento entre tabelas"
    );
    apiLogger.groupEnd();
  }
};

export const debugNetworkRequest = (
  url: string,
  method: string,
  data?: any
) => {
  apiLogger.group(`Network Request - ${method.toUpperCase()} ${url}`);
  apiLogger.log("URL:", url);
  apiLogger.log("Method:", method);
  apiLogger.log("Full URL:", `${window.location.origin}/api${url}`);
  if (data) {
    apiLogger.log("Data:", data);
  }
  apiLogger.log("Timestamp:", new Date().toISOString());
  apiLogger.log("User Agent:", navigator.userAgent);
  apiLogger.groupEnd();
};

export const debugBackendHealth = async () => {
  apiLogger.group("Backend Health Check");

  try {
    // Verificar se o backend está respondendo
    const response = await fetch("http://localhost:3333/api/auth/me", {
      credentials: "include",
    });

    apiLogger.log("Auth endpoint status:", response.status);

    if (response.ok) {
      apiLogger.log("Backend está respondendo");
      apiLogger.log("Autenticação está funcionando");
    } else {
      apiLogger.warn("Problema na autenticação");
    }
  } catch (error) {
    apiLogger.error("Backend não está respondendo:", error);
  }

  apiLogger.groupEnd();
};

// Função para testar endpoint específico
export const testConversationsEndpoint = async () => {
  apiLogger.group("Test Conversations Endpoint");

  try {
    const response = await fetch("http://localhost:3333/api/conversations", {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    apiLogger.log("Status:", response.status);
    apiLogger.log("Status Text:", response.statusText);
    apiLogger.log("Headers:", Object.fromEntries(response.headers.entries()));

    const text = await response.text();
    apiLogger.log("Raw Response:", text);

    if (response.headers.get("content-type")?.includes("application/json")) {
      try {
        const json = JSON.parse(text);
        apiLogger.log("Parsed JSON:", json);
      } catch (e) {
        apiLogger.error("Failed to parse JSON:", e);
      }
    }
  } catch (error) {
    apiLogger.error("Network Error:", error);
  }

  apiLogger.groupEnd();
};
